import type { ProxyQueryExpression } from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    addDays,
    addMonths,
    addWeeks,
    format,
    isBefore,
    isValid,
    setDate,
    startOfMonth,
    subMonths,
} from "date-fns";
import parse from "date-fns/parse";
import { utcToZonedTime } from "date-fns-tz";
import { capitalize, isEmpty } from "lodash";

import { REFRESH_CADENCE_SLA_CONFIG } from "../config/insights.config";
import {
    ALL_FAMILIES,
    InsightFamilyNameToLabelMap,
} from "../constants/filters";
import { discoveryFilterStore } from "../store/filters";
import type { DiscoveryFilterField } from "../types/filters/common";
import type { InsightFamiliesFilterField } from "../types/filters/insightFamilies";
import { RefreshCadence } from "../types/insightFamilyRefreshCadence";
import { InsightFamilyName } from "../types/providers";

import {
    DateFormat,
    formatDate,
    isValidDateString,
    Timezone,
} from "./date.utils";
import type { FilterOptionsMap } from "./filters.utils";

export type InsightFamiliesFilters = {
    [InsightFamiliesFilterField.BRAND]: FilterOptionsMap;
    [InsightFamiliesFilterField.BROWSE_NODE]: FilterOptionsMap;
    [InsightFamiliesFilterField.OBJECTIVE]: FilterOptionsMap;
};

export const formatFamilyName = (
    familyName: string | undefined,
): string | undefined => {
    return familyName && InsightFamilyNameToLabelMap[familyName]
        ? InsightFamilyNameToLabelMap[familyName]
        : undefined;
};

export const formatObjectives = (
    objectives: string[] | undefined,
): string | undefined => {
    return !isEmpty(objectives) ? objectives?.join(", ") : undefined;
};

export const DATE_FORMAT_ERROR_MESSAGE = "(Unable to format provided date)";

export const formatStartEndDate = (
    startDate: string | undefined,
    endDate: string | undefined,
): string | undefined => {
    if (!isEmpty(startDate) && !isEmpty(endDate)) {
        // startDate & endDate provided as date string formatted as "YYYY-mm-dd"
        // When parsing date without times, it is implied to be UTC
        // To avoid timezone related issues (showing dates as preceding date, etc), date is formatted to be in UTC
        return isValidDateString(startDate!) && isValidDateString(endDate!)
            ? `${formatDate(startDate, DateFormat.LONG_DATE_FORMAT, Timezone.UTC)} to ${formatDate(endDate, DateFormat.LONG_DATE_FORMAT, Timezone.UTC)}`
            : `${startDate} to ${endDate} ${DATE_FORMAT_ERROR_MESSAGE}`;
    } else return undefined;
};

export const formatUploadDate = (
    uploadDate: string | undefined,
): string | undefined => {
    if (!isEmpty(uploadDate)) {
        return isValidDateString(uploadDate!)
            ? formatDate(uploadDate, DateFormat.LONG_DATE_FORMAT)
            : `${uploadDate} ${DATE_FORMAT_ERROR_MESSAGE}`;
    } else return undefined;
};

export const getRefreshDate = (
    date: string | undefined,
    cadence: string,
): string | undefined => {
    if (date) {
        let dateObj = parse(date, "yyyyMMdd", new Date());
        const refreshCadence = RefreshCadence[cadence];
        switch (refreshCadence) {
            case RefreshCadence.DAILY:
                dateObj = addDays(dateObj, 1);
                break;
            case RefreshCadence.HOURLY:
                break;
            case RefreshCadence.MONTHLY:
                dateObj = addMonths(dateObj, 1);
                break;
            case RefreshCadence.ON_DEMAND:
                return "On demand";
            case RefreshCadence.QUARTERLY:
                dateObj = addMonths(dateObj, 3);
                break;
            case RefreshCadence.WEEKLY:
                dateObj = addWeeks(dateObj, 1);
                break;
            default:
                return undefined;
        }
        return `${format(dateObj, "yyyy-MM-dd")} (${capitalize(refreshCadence)})`;
    }
    return undefined;
};

export const isRefreshRequired = (
    datasetDate: string | undefined,
    currentDate: Date,
    cadence: RefreshCadence,
): boolean => {
    if (
        !datasetDate ||
        !currentDate ||
        !cadence ||
        !(currentDate instanceof Date)
    ) {
        console.error("Invalid parameters provided to isRefreshRequired");
        return false;
    }

    try {
        const parsedDatasetDate = parse(datasetDate, "yyyyMMdd", new Date());
        const expectedDatasetDate = getExpectedDatasetDate(
            currentDate,
            cadence,
        );
        if (!expectedDatasetDate) return false;

        return isBefore(startOfMonth(parsedDatasetDate), expectedDatasetDate);
    } catch (error) {
        console.error("Error calculating refresh need:", error);
        return false;
    }
};

export const getPlannedRefreshDate = (
    datasetDate: string,
    cadence: RefreshCadence,
    isRefreshRequired: boolean,
): Date | undefined => {
    if (!datasetDate || !cadence) {
        console.error("Missing required parameters for getPlannedRefreshDate");
        return undefined;
    }
    const normalizedCadence = cadence.toUpperCase();
    const slaConfig = REFRESH_CADENCE_SLA_CONFIG[normalizedCadence];
    if (!slaConfig) {
        console.error(`No SLA configuration found for cadence: ${cadence}`);
        return undefined;
    }

    try {
        const parsedDatasetDate = parse(datasetDate, "yyyyMMdd", new Date());
        if (!isValid(parsedDatasetDate)) return undefined;

        switch (normalizedCadence as RefreshCadence) {
            case RefreshCadence.MONTHLY: {
                const monthsForward = isRefreshRequired ? 2 : 0;
                const expectedRefresh = addMonths(
                    parsedDatasetDate,
                    monthsForward,
                );
                return setDate(expectedRefresh, slaConfig.refreshDay);
            }
            default:
                return undefined;
        }
    } catch (error) {
        console.error("Error calculating refresh need:", error);
        return undefined;
    }
};

export const formatCadence = (cadence: RefreshCadence): string => {
    return cadence.replace(/_/g, " ");
};

export const getExpectedDatasetDate = (
    currentDate: Date,
    cadence: RefreshCadence,
): Date | undefined => {
    const normalizedCadence = cadence.toUpperCase();
    const slaConfig = REFRESH_CADENCE_SLA_CONFIG[normalizedCadence];
    if (!slaConfig) {
        console.error(`No SLA configuration found for cadence: ${cadence}`);
        return undefined;
    }

    // Get UTC day to ensure consistent day calculation across timezones
    const utcDate = utcToZonedTime(currentDate, Timezone.UTC);

    switch (normalizedCadence as RefreshCadence) {
        // TODO: Expand to other refresh cadences
        case RefreshCadence.MONTHLY: {
            /**
            Get the expected dataset date based on the SLA cutoff.
            - If current date is on or before the 17th, expected data is from two months ago.
            - If after the 17th, expected data is from last month.
            - Retreive start of month since logic only compares on month
            */
            const monthsBack =
                utcDate.getDate() <= slaConfig.refreshDay ? 2 : 1;
            return startOfMonth(subMonths(utcDate, monthsBack));
        }
        default:
            return undefined;
    }
};
/**
 * Stores the string replacements we want to make on the search query to help users find more relevant content
 * e.g. "Share Of Voice" -> "ShareOfVoice"
 */
const SearchReplacements: Record<string, string> = {
    ...Object.values(InsightFamilyName).reduce(
        (acc, k) => ({
            ...acc,
            [InsightFamilyNameToLabelMap[k]]: k,
        }),
        {},
    ),
};

/**
 * Converts the current filter state into the queryExpression needed to send to the IStore backend
 * @returns The QueryExpression needed for the IStore APIs
 */
export const getInsightFamilyFilterQueryExpression = (
    ignoreField?: DiscoveryFilterField,
    ignoreFamily?: boolean,
): ProxyQueryExpression => {
    const state = discoveryFilterStore.getState();
    const [subType, searchQuery, filters] = [
        state.getContentSubType(),
        state.getInsightsFilters().INSIGHT_FAMILIES.searchQuery,
        { ...state.getInsightsFilters().INSIGHT_FAMILIES.fieldSelections },
    ];

    // Perform common replacements within our search query
    let modifiedQuery = searchQuery;
    Object.entries(SearchReplacements).forEach(([key, value]) => {
        const regEx = new RegExp(key, "ig");
        modifiedQuery = modifiedQuery.replace(regEx, value);
    });

    // Optionally ignore certain filters that are applied
    if (ignoreField) {
        filters[ignoreField] = [];
    }

    // The names in istore's OpenSearch database are different than our enum, so we have to map them
    const apiToFieldMappings: Record<string, (string | number)[]> = {
        brand_name: filters.FAMILY_BRAND.map((i) => i.label),
        browse_node_name: filters.FAMILY_BROWSE_NODE.map((i) => i.label),
    };

    // Only add artifact_name if subType.scope exists and is not "All"
    if (subType.scope && subType.scope !== ALL_FAMILIES && !ignoreFamily) {
        apiToFieldMappings.artifact_name = [subType.scope];
    }

    // Build an exact expression based on what filters the user has selected
    const exactExpression = Object.entries(apiToFieldMappings)
        .map(([name, vals]) =>
            vals.map((v) => `${name} == "${v}"`).join(" || "),
        )
        .filter((o) => o)
        .map((e) => `(${e})`)
        .join(" && ");

    // Build fuzzy expression for objectives only
    const fuzzyExpression =
        filters.FAMILY_OBJECTIVE && filters.FAMILY_OBJECTIVE.length > 0
            ? `(${filters.FAMILY_OBJECTIVE.map(
                  (obj) => `objectives == "${obj.label}"`,
              ).join(" || ")})`
            : "";

    return {
        searchQuery: modifiedQuery ?? "",
        exactExpression,
        fuzzyExpression,
        operator: "AND",
    };
};
